import { createContext, useContext } from "react";
import type { Member } from "@/types/Member";
import type { Location } from "@/types/Location";
import type { Room } from "@/types/Room";
import type { Booking } from "@/types/Booking";
import type { Amenity } from "@/types/Amenity";

interface CalendarContextType {
  slug: string;
  member: Member | null;

  // UI state (managed by LocationRoomSchedule)
  date: Date | null;
  setDate: (date: Date | null) => void;
  calendarType: "day" | "week";
  setCalendarType: (type: "day" | "week") => void;
  selectedLocation: Location | null;
  setSelectedLocation: (location: Location | null) => void;
  selectedRoom: Room | null;
  setSelectedRoom: (room: Room | null) => void;

  // Filter state
  selectedRooms: Room[];
  setSelectedRooms: (rooms: Room[]) => void;
  selectedAmenities: Amenity[];
  setSelectedAmenities: (amenities: Amenity[]) => void;
  availableAmenities: Amenity[];

  // Room-amenity filter (auto-narrows displayed rooms to those with ALL selected amenities, by name)
  selectedRoomAmenities: string[];
  setSelectedRoomAmenities: (names: string[]) => void;
  availableRoomAmenities: string[];

  // Data from React Query (read-only)
  availableLocations: Location[] | null;
  availableRooms: Room[] | null;
  bookings: Booking[] | null;

  // Loading states from React Query (read-only)
  isLoading: boolean;
  isDateLoading: boolean;
  isLocationLoading: boolean;
  isRoomLoading: boolean;

  // Refetch from React Query
  refreshBookings?: () => void;
}

export const CalendarContext = createContext<CalendarContextType | undefined>(undefined);

export const useCalendar = () => {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error("useCalendar must be used within a CalendarProvider");
  }
  return context;
};
